import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Button, Flex, HStack, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Tooltip, useDisclosure } from '@chakra-ui/react'
import { deleteDisplayPricing, exportDisplayPricings, getDisplayPricings, importDisplayPricings } from 'api'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'
import { getAssetUrl } from 'lib'

const BreadcrumbNav = [
  {
    label: 'Display Pricings',
    isCurrentPage: true
  }
]

const DISPLAY_PRICINGS_COLUMNS = [
  {
    label: 'Key No',
    orderBy: 'keyNo',
    render: ({ keyNo }) => keyNo
  },
  {
    label: 'Pitch',
    orderBy: 'pitch',
    render: ({ pitch }) => pitch
  },
  {
    label: 'Board Matrix',
    orderBy: 'boardMatrix',
    render: ({ boardMatrix }) => boardMatrix
  },
  {
    label: 'Module H',
    orderBy: 'modsH',
    render: ({ modsH }) => modsH
  },
  {
    label: 'Module L',
    orderBy: 'modsL',
    render: ({ modsL }) => modsL
  },
  {
    label: 'DIMS H',
    orderBy: 'dimsH',
    render: ({ dimsH }) => dimsH
  },
  {
    label: 'DIMS W',
    orderBy: 'dimsW',
    render: ({ dimsW }) => dimsW
  },
  {
    label: 'SF Price',
    orderBy: 'sfPrice',
    render: ({ sfPrice }) => sfPrice
  },
  {
    label: 'DF Price',
    orderBy: 'dfPrice',
    render: ({ dfPrice }) => dfPrice
  },
  {
    label: 'Actions',
    render: ({ onRemove, ...rest }) => (
      <Flex columnGap={2}>
        <Button as={NavLink} to={`/system/display-pricings/edit/${rest.id}`} size="xs" colorScheme="blue">Edit</Button>
        <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button>
      </Flex>
    )
  }
]

export const DisplayPricingList = () => {
  const navigate = useNavigate()
  const disclosure = useDisclosure()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'keyNo',
      direction: 'asc'
    }
  })
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedPricing, setSelectedPricing] = useState(null)
  const fileInputRef = useRef(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getDisplayPricings(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (pricing) => {
    setSelectedPricing(pricing)
    disclosure.onOpen()
  }

  const handleRemove = async (pricingId) => {
    setIsRemoving(true)
    try {
      await deleteDisplayPricing(pricingId)
      setIsRemoving(false)
      disclosure.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onRemove }))
  }, [rows])

  const onImport = () => {
    fileInputRef.current?.click()
  }

  const handleImport = async (e) => {
    const file = e.target.files?.[0]

    if (file) {
      const data = new FormData()
      data.append('file', file)

      setIsImporting(true)

      try {
        const { insertedRows } = await importDisplayPricings(data)
        createToast('success', `${insertedRows} rows inserted.`)
        setIsImporting(false)
        load()
      } catch (err) {
        createToast('error', 'Error', err.response.data)
        setIsImporting(false)
      }
    }
  }

  const handleExport = async () => {
    setIsExporting(true)

    try {
      const { path } = await exportDisplayPricings()

      const a = document.createElement('a')
      a.href = getAssetUrl(path)
      a.download = 'display-pricings.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      setIsExporting(false)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsExporting(false)
    }
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Input ref={fileInputRef} type="file" onChange={handleImport} accept="text/csv" hidden />
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Display Pricings</Heading>
          <HStack gap={4}>
            <Tooltip
              hasArrow
              label='Make sure the csv file has a standard format like following. First row should be header which represents each column. The data should be written in an appropriate order - Key No, Pitch, Board Matrix, Calculated Board Matrix, No of Lines, No of Characters, Module Height, Module Length, DIMS H, DIMS W, SF Price, DF Price, Typical Amps 120v, Max Amps 120v, Typical Amps 240v, Max Amps 240v'
            >
              <Button
                size="md"
                onClick={onImport}
                isLoading={isImporting}
              >
                Import
              </Button>
            </Tooltip>
            <Button
              size="md"
              onClick={handleExport}
              isLoading={isExporting}
            >
              Export
            </Button>
            <Button
              size="md"
              colorScheme="blue"
              onClick={() => navigate('/system/display-pricings/add')}
              leftIcon={<MdAdd size="24px" color="white" />}
            >
              Add Pricing
            </Button>
          </HStack>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={DISPLAY_PRICINGS_COLUMNS}
          isLoading={isLoading}
        />
      </Box>
      <ModalRemoveDisplayPricing
        pricing={selectedPricing}
        isRemoving={isRemoving}
        handleRemove={() => handleRemove(selectedPricing?.id)}
        {...disclosure}
      />
    </>
  )
}

const ModalRemoveDisplayPricing = ({ pricing, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Pricing</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the pricing - {pricing?.keyNo}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveDisplayPricing.propTypes = {
  pricing: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
